import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Enquanto o cálculo roda no backend, a tela consulta de novo neste intervalo.
const POLL_MS = 10000;

// Espaço em disco por empresa (somente super usuário). `enabled` = false não
// faz nenhuma requisição (o endpoint responde 401 para quem não é super).
const useCompanyStorage = (enabled = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    // Uma consulta por vez: a anterior é abortada para não ocupar conexão.
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    try {
      const { data: result } = await api.get("/companies/storage", {
        signal: controller.signal,
      });
      setData(result);
    } catch (err) {
      if (!controller.signal.aborted) {
        toast.error(i18n.t("companyStorage.loadError"));
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled || !data || !data.running) return undefined;
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [enabled, data, load]);

  const refresh = async () => {
    setRequesting(true);
    try {
      await api.post("/companies/storage/refresh");
      toast.info(i18n.t("companyStorage.refreshStarted"));
      setData((prev) => ({ ...(prev || {}), running: true }));
    } catch (err) {
      toastError(err);
      // 409 = outro cálculo já está rodando: sincroniza o estado da tela.
      load();
    }
    setRequesting(false);
  };

  return { data, loading, requesting, refresh, reload: load };
};

export default useCompanyStorage;
